/**
 * @file OrderDetailsDrawer.tsx
 *
 * Read-only order detail panel shown in the {@link SideDrawer}, mirroring the
 * WSB-v2 OrdersTab detail panel. Summarises the customer (with an initials
 * avatar), order id / payment / date / status, line items, totals, coupons, and
 * the billing / shipping addresses. The footer carries always-present Delete and
 * Edit status actions plus state-gated Cancel / Fulfil / Mark paid / Refund (all open
 * the parent's dialogs; the engine gates Delete to cancelled / completed orders and
 * 404s the status override while its kill switch is off). The ref is forwarded to the drawer
 * root so the parent can dismiss it on outside click, and the last selected order is
 * kept mounted so the close animation has content.
 */

import { forwardRef } from 'react';
import { Divider, Icon, Initials } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopOrder } from '../api/shop-types';
import { SideDrawer } from '../components/SideDrawer';
import { StatusBadge } from '../components/StatusBadge';
import {
  canCancelOrder,
  canFulfilOrder,
  canMarkOrderPaid,
  canRefundOrder,
  customerName,
  formatAddress,
  formatOrderDate,
  formatOrderTime,
  hasAmount,
  orderItemCount,
} from './order-list';
import { statusIndicator, statusLabel } from './order-status';
import styles from './OrdersView.module.css';

interface OrderDetailsDrawerProps {
  order: ShopOrder | null;
  isOpen: boolean;
  currencySymbol: string;
  onClose: () => void;
  onCancel: () => void;
  onRefund: () => void;
  onDelete: () => void;
  onMarkPaid: () => void;
  onFulfil: () => void;
  onEditStatus: () => void;
}

/**
 * Renders the order detail drawer.
 *
 * @param props - The order to show, open/currency state, and the
 *   close/cancel/refund/delete/mark-paid/fulfil/edit-status handlers.
 * @param ref - Forwarded to the drawer root for outside-click detection.
 *
 * @returns The drawer element.
 */
export const OrderDetailsDrawer = forwardRef<HTMLDivElement, OrderDetailsDrawerProps>(
  function OrderDetailsDrawer(
    {
      order,
      isOpen,
      currencySymbol,
      onClose,
      onCancel,
      onRefund,
      onDelete,
      onMarkPaid,
      onFulfil,
      onEditStatus,
    },
    ref,
  ) {
    const intl = useIntl();

    if (!order) {
      return (
        <SideDrawer
          ref={ref}
          isOpen={false}
          testId="order-details-drawer"
          children={null}
        />
      );
    }

    const labelDesc = statusLabel(order.status);
    const statusText = labelDesc ? intl.formatMessage(labelDesc) : order.status;
    const amount = (value: string): string =>
      `${order.currency_symbol || currencySymbol}${value}`;
    const initials =
      [order.billing.first_name, order.billing.last_name]
        .filter(Boolean)
        .map((name) => name[0].toUpperCase())
        .join('') || '#';
    const shippingName = `${order.shipping.first_name} ${order.shipping.last_name}`.trim();

    return (
      <SideDrawer
        ref={ref}
        isOpen={isOpen}
        label={customerName(order)}
        onClose={onClose}
        testId="order-details-drawer"
      >
        <div className="gv-w-full gv-h-full gv-flex gv-flex-col">
          <div className="gv-flex gv-flex-col gv-p-lg">
            <div className={styles.sidepanelHeader}>
              <div className={styles.sidepanelHeaderLeft}>
                <Initials nameInitials={initials} />
                <div className={styles.headerInfo}>
                  <span className="gv-text-sm gv-text-bold">{customerName(order)}</span>
                  <span className={`gv-caption-sm ${styles.muted}`}>
                    <FormattedMessage
                      {...adminMessages.ordersItemsCount}
                      values={{ count: orderItemCount(order) }}
                    />
                  </span>
                </div>
              </div>
              <button
                className="gv-button gv-button-icon gv-mode-condensed"
                type="button"
                data-testid="order-details-close"
                onClick={onClose}
                aria-label={intl.formatMessage(adminMessages.ordersClose)}
              >
                <Icon name="close" size="small" />
              </button>
            </div>
            <div className="gv-pt-md">
              <Divider />
            </div>
          </div>

          <div className={styles.sidepanelBody}>
            <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
              <div className="gv-flex gv-flex-row">
                <div className="gv-flex gv-flex-col gv-flex-1">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersDrawerOrderId} />
                  </span>
                  <span className="gv-text-sm">{order.number || '—'}</span>
                </div>
                <div className="gv-flex gv-flex-col gv-flex-1">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersDrawerPayment} />
                  </span>
                  <span className="gv-text-sm">{order.payment_method_title || '—'}</span>
                </div>
              </div>
              <div className="gv-flex gv-flex-col gv-gap-md">
                <div className="gv-flex gv-flex-col gv-flex-1">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersColDate} />
                  </span>
                  <span className="gv-text-sm">
                    {formatOrderDate(order.date_created)}, {formatOrderTime(order.date_created)}
                  </span>
                </div>
                <div className="gv-flex gv-flex-col gv-flex-1">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersColStatus} />
                  </span>
                  <StatusBadge indicator={statusIndicator(order.status)} label={statusText} />
                </div>
              </div>
            </div>

            <div className="gv-py-md">
              <Divider />
            </div>

            <div className="gv-flex gv-flex-col gv-gap-sm">
              <span className="gv-text-sm gv-text-bold">
                <FormattedMessage {...adminMessages.ordersDrawerItems} />
              </span>
              <div className={`${styles.itemsList} gv-gap-sm`}>
                {order.line_items.map((item) => (
                  <div key={item.id} className="gv-flex gv-items-center gv-gap-sm">
                    <div className={styles.itemThumbWrap}>
                      {item.image ? (
                        <img className={styles.itemThumb} src={item.image} alt={item.name} />
                      ) : (
                        <div className={styles.itemThumbEmpty} />
                      )}
                    </div>
                    <div className={styles.itemInfo}>
                      <span className="gv-text-sm">{item.name}</span>
                      {item.sku && (
                        <span className={`gv-caption-sm ${styles.muted}`}>
                          <FormattedMessage {...adminMessages.productsFieldSku} />: {item.sku}
                        </span>
                      )}
                    </div>
                    <div className={`gv-flex gv-flex-col gv-items-center ${styles.itemQty}`}>
                      <span className="gv-text-sm">
                        <FormattedMessage
                          {...adminMessages.ordersDrawerQuantity}
                          values={{ quantity: item.quantity }}
                        />
                      </span>
                    </div>
                    <div className={styles.itemRight}>
                      <span className="gv-text-sm">{amount(item.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="gv-py-md">
              <Divider />
            </div>

            <div className="gv-flex gv-flex-col gv-gap-sm">
              {hasAmount(order.discount_total) && (
                <div className={styles.totalRow}>
                  <span className="gv-text-sm">
                    <FormattedMessage {...adminMessages.ordersDrawerDiscount} />
                  </span>
                  <span className="gv-text-sm">-{amount(order.discount_total)}</span>
                </div>
              )}
              {hasAmount(order.shipping_total) && (
                <div className={styles.totalRow}>
                  <span className="gv-text-sm">
                    <FormattedMessage {...adminMessages.ordersDrawerShipping} />
                  </span>
                  <span className="gv-text-sm">{amount(order.shipping_total)}</span>
                </div>
              )}
              {hasAmount(order.total_tax) && (
                <div className={styles.totalRow}>
                  <span className="gv-text-sm">
                    <FormattedMessage {...adminMessages.ordersDrawerTax} />
                  </span>
                  <span className="gv-text-sm">{amount(order.total_tax)}</span>
                </div>
              )}
              <div className={styles.totalRow}>
                <span className="gv-text-sm gv-text-bold">
                  <FormattedMessage {...adminMessages.ordersDrawerTotal} />
                </span>
                <span className="gv-text-sm gv-text-bold">{amount(order.total)}</span>
              </div>
            </div>

            {order.coupon_lines.length > 0 && (
              <>
                <div className="gv-py-md">
                  <Divider />
                </div>
                <div className="gv-flex gv-flex-col">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersDrawerCoupons} />
                  </span>
                  <div className="gv-flex gv-flex-col gv-gap-sm">
                    {order.coupon_lines.map((coupon) => (
                      <span
                        key={coupon.code}
                        className="gv-text-sm gv-text-signal-positive"
                      >
                        {coupon.code}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="gv-py-md">
              <Divider />
            </div>

            <div className="gv-flex gv-flex-col">
              <span className="gv-text-sm gv-text-bold">
                <FormattedMessage {...adminMessages.ordersDrawerBilling} />
              </span>
              <div className="gv-flex gv-flex-col">
                <span className="gv-text-sm">{customerName(order)}</span>
                {order.billing.email && (
                  <span className="gv-text-sm">{order.billing.email}</span>
                )}
                {order.billing.phone && (
                  <span className="gv-text-sm">{order.billing.phone}</span>
                )}
                {order.billing.address_1 && (
                  <span className="gv-text-sm">{formatAddress(order.billing)}</span>
                )}
              </div>
            </div>

            {order.shipping.address_1 && (
              <>
                <div className="gv-py-md">
                  <Divider />
                </div>
                <div className="gv-flex gv-flex-col">
                  <span className="gv-text-sm gv-text-bold">
                    <FormattedMessage {...adminMessages.ordersDrawerShippingAddress} />
                  </span>
                  <div className="gv-flex gv-flex-col">
                    {shippingName && <span className="gv-text-sm">{shippingName}</span>}
                    <span className="gv-text-sm">{formatAddress(order.shipping)}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.sidepanelFooter}>
            <button
              className="gv-button gv-button-destructive"
              type="button"
              data-testid="order-details-delete"
              onClick={onDelete}
            >
              <FormattedMessage {...adminMessages.ordersMenuDelete} />
            </button>
            {canCancelOrder(order) && (
              <button
                className="gv-button gv-button-cancel"
                type="button"
                data-testid="order-details-cancel"
                onClick={onCancel}
              >
                <FormattedMessage {...adminMessages.ordersMenuCancel} />
              </button>
            )}
            <button
              className="gv-button gv-button-cancel"
              type="button"
              data-testid="order-details-edit-status"
              onClick={onEditStatus}
            >
              <FormattedMessage {...adminMessages.ordersEditStatus} />
            </button>
            {canFulfilOrder(order) && (
              <button
                className="gv-button gv-button-cancel"
                type="button"
                data-testid="order-details-fulfil"
                onClick={onFulfil}
              >
                <FormattedMessage {...adminMessages.ordersFulfil} />
              </button>
            )}
            {canMarkOrderPaid(order) && (
              <button
                className="gv-button gv-button-primary"
                type="button"
                data-testid="order-details-mark-paid"
                onClick={onMarkPaid}
              >
                <FormattedMessage {...adminMessages.ordersMarkPaid} />
              </button>
            )}
            {canRefundOrder(order) && (
              <button
                className="gv-button gv-button-primary"
                type="button"
                data-testid="order-details-refund"
                onClick={onRefund}
              >
                <FormattedMessage {...adminMessages.ordersMenuRefund} />
              </button>
            )}
          </div>
        </div>
      </SideDrawer>
    );
  },
);
